/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Component for Allocation Summary Overview Button
 */
import { useMemo, useState } from 'react';
import { Button, ButtonProps, Modal, Spin } from 'antd';

import useGetPaymentAllocation from 'hooks/payments/useGetPaymentAllocation';
import { renderAmount } from 'utils/claims/fhir';
import { getParsedPaymentReconciliation } from 'utils/payment/payments';
import PaymentSummaryTable from 'components/payments/allocationSummary/PaymentSummaryTable';
import { EMPTY_VALUE } from '@veritable/common/constants';

type IProps = ButtonProps & {
	id: string;
	allocatedAmount: number;
};
/**
 * Allocation Summary Overview Button
 */
const AllocatedOverview: React.FC<IProps> = ({ id, allocatedAmount, ...restProps }) => {
	const [open, setOpen] = useState(false);
	const { data: allocationData, isLoading } = useGetPaymentAllocation(id, {
		enabled: !!id && open,
	});

	const parsedAllocationData = useMemo(() => {
		return getParsedPaymentReconciliation(
			allocationData?.fhirResource as Fhir.PaymentReconciliation,
			allocationData?.fhirGuarantor as Fhir.Patient | Fhir.RelatedPerson | Fhir.Organization,
			allocationData?.attachments,
		);
	}, [allocationData]);

	if (!allocatedAmount) {
		return EMPTY_VALUE;
	}

	return (
		<>
			<Button type='link' {...restProps} onClick={() => setOpen(true)} style={{ padding: 0 }}>
				{renderAmount(allocatedAmount)}
			</Button>
			<Modal
				title={'Allocations'}
				open={open}
				destroyOnHidden
				closable={true}
				onCancel={() => setOpen(false)}
				footer={null}
				width={'70%'}
				maskClosable={false}
			>
				<Spin spinning={isLoading}>
					<PaymentSummaryTable
						data={allocationData}
						parsedAllocationData={parsedAllocationData}
						id={id}
						allowDelete={false}
						showTitle={false}
					/>
				</Spin>
			</Modal>
		</>
	);
};

export default AllocatedOverview;
